export interface Node {
  rank?: number;
  low?: number;
  lim?: number;
  parent?: string;
}

export interface Edge {
  weight: number;
  minlen: number;
  cutvalue?: number;
}

export class Graph {
  private nodesMap = new Map<string, Node>();
  private edgesMap = new Map<string, Map<string, Edge>>();
  private edgeList: { v: string; w: string }[] = [];
  private directed: boolean;

  constructor(directed = true) {
    this.directed = directed;
  }

  setNode(v: string, attrs: Node = {}): void {
    if (!this.nodesMap.has(v)) {
      this.nodesMap.set(v, attrs);
      this.edgesMap.set(v, new Map());
    } else {
      Object.assign(this.nodesMap.get(v)!, attrs);
    }
  }

  node(v: string): Node {
    const node = this.nodesMap.get(v);
    if (!node) throw new Error(`Node ${v} not found`);
    return node;
  }

  nodes(): string[] {
    return Array.from(this.nodesMap.keys());
  }

  hasNode(v: string): boolean {
    return this.nodesMap.has(v);
  }

  setEdge(v: string, w: string, attrs: Partial<Edge> = {}): void {
    this.setNode(v);
    this.setNode(w);
    const edge: Edge = { weight: 1, minlen: 1, ...attrs };
    if (!this.edgesMap.get(v)!.has(w)) {
      this.edgeList.push({ v, w });
    }
    this.edgesMap.get(v)!.set(w, edge);
    if (!this.directed) {
      if (!this.edgesMap.get(w)!.has(v)) {
        this.edgeList.push({ v: w, w: v });
      }
      this.edgesMap.get(w)!.set(v, edge);
    }
  }

  edge(v: string, w: string): Edge | undefined {
    return this.edgesMap.get(v)?.get(w);
  }

  hasEdge(v: string, w: string): boolean {
    return Boolean(this.edge(v, w));
  }

  edges(): { v: string; w: string }[] {
    return this.edgeList;
  }

  nodeEdges(v: string): { v: string; w: string }[] {
    const edges: { v: string; w: string }[] = [];
    const out = this.edgesMap.get(v);
    if (out) {
      for (const [w, _] of out) {
        edges.push({ v, w });
      }
    }
    if (this.directed) {
      // need to check incoming edges
      for (const [src, targets] of this.edgesMap) {
        if (targets.has(v) && src !== v) {
          edges.push({ v: src, w: v });
        }
      }
    }
    return edges;
  }

  outEdges(v: string): { v: string; w: string }[] {
    const edges: { v: string; w: string }[] = [];
    const out = this.edgesMap.get(v);
    if (out) {
      for (const [w, _] of out) {
        edges.push({ v, w });
      }
    }
    return edges;
  }

  inEdges(v: string): { v: string; w: string }[] {
    const edges: { v: string; w: string }[] = [];
    for (const [src, targets] of this.edgesMap) {
      if (targets.has(v)) {
        edges.push({ v: src, w: v });
      }
    }
    return edges;
  }

  neighbors(v: string): string[] {
    const neigh = new Set<string>();
    const out = this.edgesMap.get(v);
    if (out) {
      for (const [w, _] of out) {
        neigh.add(w);
      }
    }
    if (this.directed) {
      for (const [src, targets] of this.edgesMap) {
        if (targets.has(v) && src !== v) {
          neigh.add(src);
        }
      }
    }
    return Array.from(neigh);
  }

  removeEdge(v: string, w: string): void {
    const out = this.edgesMap.get(v);
    if (out?.has(w)) {
      out.delete(w);
      // Remove from edgeList
      const idx = this.edgeList.findIndex((e) => e.v === v && e.w === w);
      if (idx >= 0) this.edgeList.splice(idx, 1);
      if (!this.directed) {
        const out2 = this.edgesMap.get(w);
        if (out2?.has(v)) {
          out2.delete(v);
          const idx2 = this.edgeList.findIndex((e) => e.v === w && e.w === v);
          if (idx2 >= 0) this.edgeList.splice(idx2, 1);
        }
      }
    }
  }

  nodeCount(): number {
    return this.nodesMap.size;
  }

  edgeCount(): number {
    return this.edgeList.length;
  }
}
